import LineChart from '@/components/charts/LineChart.tsx';
import {
  seriesEndValue,
  timeSeriesToLineSeries,
} from '@/components/charts/timeSeriesAdapter.ts';
import {
  InfoCard,
  InfoCardBody,
  InfoCardHeader,
} from '@/components/ui/InfoCard.tsx';
import HeadlineNumber from '@/components/ui/HeadlineNumber.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import { TimeSeriesDto } from '@/types/stats/TimeSeriesDto.ts';
import { AvailabilityFieldDto } from '@/features/dashboard/tours/types/AvailabilityFieldDto.ts';
import {
  SIGNUP_SERIES_REGISTRY,
  SignupSeriesKey,
} from '@/features/dashboard/tours/types/sales/signupSeries.utils.ts';
import { pluralForm } from '@/utils/pluralize.ts';

interface TourSignupPaceCardProps {
  signups: TimeSeriesDto;
  occupancy: AvailabilityFieldDto;
}

/**
 * Zapisy narastająco i prognoza na jednej osi.
 *
 * Dopisek składa się z tego, co przyjechało: prognoza bywa pusta, benchmarku
 * Podsumowanie w ogóle nie dostaje — każdy brak zabiera swój człon, zamiast
 * zostawiać puste „prognoza —”.
 */
const TourSignupPaceCard = ({
  signups,
  occupancy,
}: TourSignupPaceCardProps) => {
  const { currentCount, limit } = occupancy;
  const series = timeSeriesToLineSeries(signups, SIGNUP_SERIES_REGISTRY);

  const forecastEnd = seriesEndValue(signups, SignupSeriesKey.FORECAST);
  const benchmarkEnd = seriesEndValue(signups, SignupSeriesKey.BENCHMARK);

  const note = [
    limit === null
      ? null
      : `z ${limit} ${pluralForm(limit, 'miejsca', 'miejsc', 'miejsc')}`,
    forecastEnd === null ? null : `prognoza ${forecastEnd}`,
    benchmarkEnd === null ? null : `średnia innych wyjazdów ${benchmarkEnd}`,
  ]
    .filter((clause) => clause !== null)
    .join(' · ');

  return (
    <InfoCard>
      <InfoCardHeader title="Tempo zapisów" />
      <InfoCardBody className="gap-4">
        <div>
          <HeadlineNumber className="block">{currentCount}</HeadlineNumber>
          {note && (
            <span className="mt-0.5 block text-sm text-neutral-500">
              {note}
            </span>
          )}
        </div>

        {series.length === 0 ? (
          // Wyjazd bez zapisów nie ma czego rysować — pusta oś czyta się jak
          // zepsuty wykres, a objaśnienie prognozy obiecuje kreskę, której nie
          // ma. Licznik zostaje, bo zero też jest odpowiedzią.
          <NoItems
            title="Brak zapisów"
            description="Krzywa pojawi się po pierwszej rezerwacji."
          />
        ) : (
          <>
            <LineChart
              series={series}
              maxValue={limit}
              ariaLabel="Zapisy narastająco wraz z prognozą"
            />

            <p className="text-pretty text-sm text-neutral-700">
              Prognoza przedłuża tempo zapisów z ostatnich czterech tygodni —
              nie uwzględnia rezygnacji ani przesunięć z listy rezerwowej.
            </p>
          </>
        )}
      </InfoCardBody>
    </InfoCard>
  );
};

export default TourSignupPaceCard;
